import type { Snippet } from "../api/client";

/**
 * Auto-split a methodology (or any markdown body) into reusable section
 * snippets, then match each section against an existing snippet library so the
 * UI can show which sections already exist vs. which are new and savable.
 *
 * Everything here is pure (no I/O) so it can be unit-tested in isolation.
 */

export interface Section {
  /** Heading text with the leading `#`/`N.`/bold markers stripped. */
  heading: string;
  /** Markdown heading level (2 for `##`, 3 for `###`, …). */
  level: number;
  /** Body of the section — everything after the heading line, trimmed. */
  body: string;
  /** Full block: the heading line plus body, with trailing blanks trimmed. */
  content: string;
}

export type MatchKind = "exact" | "similar" | "new";

export interface SectionMatch {
  section: Section;
  match?: {
    snippet: Snippet;
    /** Sørensen–Dice coefficient over word tokens, in [0, 1]. */
    score: number;
    kind: MatchKind;
  };
}

// Classification thresholds (documented in the build report).
export const EXACT_THRESHOLD = 0.98;
export const SIMILAR_THRESHOLD = 0.6;

/**
 * Segment a markdown body at H2 (`## …`) headings — the primary section
 * boundary for our Notion-exported methodologies (`## N. Section Title`).
 *
 * Rules:
 * - H2 is the only split boundary. Deeper headings (H3+) stay *inside* the
 *   section they belong to, so a section's `content` is the whole block from
 *   its `##` line up to (but not including) the next `##` line.
 * - A leading preamble before the first H2 is returned as an untitled section
 *   (level 0, empty heading) only if it has non-blank content; otherwise it is
 *   dropped.
 * - Trailing blank lines are trimmed from both `body` and `content`.
 * - Fenced code blocks are respected: a `## ` *inside* a ``` fence is not a
 *   heading boundary.
 */
export function splitIntoSections(markdown: string): Section[] {
  const lines = markdown.split("\n");
  const sections: Section[] = [];

  let inFence = false;
  let fenceMarker = "";

  // Accumulators for the current block.
  let curHeadingLine: string | null = null;
  let curLevel = 0;
  let bodyLines: string[] = [];

  const flush = () => {
    // Drop an empty leading preamble (no heading + no body content).
    const bodyText = trimTrailingBlank(bodyLines.join("\n"));
    if (curHeadingLine === null && bodyText.trim() === "") {
      bodyLines = [];
      return;
    }
    const headingText =
      curHeadingLine === null ? "" : stripHeading(curHeadingLine);
    const contentLines =
      curHeadingLine === null ? bodyLines : [curHeadingLine, ...bodyLines];
    sections.push({
      heading: headingText,
      level: curLevel,
      body: bodyText,
      content: trimTrailingBlank(contentLines.join("\n")),
    });
    bodyLines = [];
  };

  for (const line of lines) {
    const fence = line.match(/^\s*(```+|~~~+)/);
    if (fence) {
      if (!inFence) {
        inFence = true;
        fenceMarker = fence[1][0];
      } else if (fence[1][0] === fenceMarker) {
        inFence = false;
        fenceMarker = "";
      }
    }

    const h2 = !inFence && /^##\s+\S/.test(line);
    if (h2) {
      // Boundary: finish the previous block, start a new one.
      flush();
      curHeadingLine = line;
      curLevel = 2;
      continue;
    }

    if (curHeadingLine === null && sections.length === 0 && curLevel === 0) {
      // Still in (or starting) the preamble.
    }
    bodyLines.push(line);
  }
  flush();

  return sections;
}

/** Remove trailing blank/whitespace-only lines from a block. */
function trimTrailingBlank(text: string): string {
  return text
    .replace(/[\s]+$/u, (m) => (m.includes("\n") ? "" : m))
    .replace(/\n+$/, "");
}

/** Strip leading `#` hashes, a `N.`/`N)` numeric prefix, and bold markers. */
function stripHeading(line: string): string {
  let h = line.replace(/^#{1,6}\s+/, "").trim();
  h = h.replace(/^\*\*(.+?)\*\*$/, "$1").trim();
  h = h.replace(/^\d+[.)]\s+/, "").trim();
  return h;
}

/**
 * Normalize text for fuzzy matching: lowercase, strip markdown heading hashes,
 * bold/italic markers and inline list bullets, then collapse all whitespace to
 * single spaces. Trivially-different copies (heading casing, extra blank lines,
 * `**bold**` vs plain) collapse to the same string.
 */
export function normalizeForMatch(text: string): string {
  return text
    .toLowerCase()
    .replace(/^#{1,6}\s+/gm, " ") // heading hashes
    .replace(/[*_`]+/g, " ") // bold / italic / code markers
    .replace(/^\s*[-*+]\s+/gm, " ") // list bullets
    .replace(/\s+/g, " ")
    .trim();
}

/** Word tokens for similarity scoring. */
function tokenize(text: string): string[] {
  const norm = normalizeForMatch(text);
  if (!norm) return [];
  return norm.split(" ").filter(Boolean);
}

/**
 * Sørensen–Dice coefficient over the *sets* of word tokens of two strings.
 * Identical token sets → 1; fully disjoint → 0; partial overlap in between.
 * Two empty strings are treated as identical (1).
 */
export function diceSimilarity(a: string, b: string): number {
  const setA = new Set(tokenize(a));
  const setB = new Set(tokenize(b));
  if (setA.size === 0 && setB.size === 0) return 1;
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersection = 0;
  for (const t of setA) if (setB.has(t)) intersection += 1;
  return (2 * intersection) / (setA.size + setB.size);
}

/**
 * Match each section to the best snippet in the library by Dice similarity of
 * normalized `content`, then classify:
 *   - `exact`   score ≥ EXACT_THRESHOLD (0.98)
 *   - `similar` score ≥ SIMILAR_THRESHOLD (0.6)
 *   - `new`     below SIMILAR_THRESHOLD (no match returned)
 */
export function matchSections(
  sections: Section[],
  library: Snippet[],
): SectionMatch[] {
  return sections.map((section) => {
    let best: { snippet: Snippet; score: number } | null = null;
    for (const snippet of library) {
      const score = diceSimilarity(section.content, snippet.content);
      if (!best || score > best.score) best = { snippet, score };
    }

    if (!best || best.score < SIMILAR_THRESHOLD) {
      return { section };
    }
    const kind: MatchKind = best.score >= EXACT_THRESHOLD ? "exact" : "similar";
    return {
      section,
      match: { snippet: best.snippet, score: best.score, kind },
    };
  });
}
